import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable, OnInit } from '@angular/core';
import { InsuranceTarget, SavingInsuranceTarget } from '@core/models/insurance-target.model';
import { LifePlanInsurances } from '@core/models/insurance.model';
import { LifePlan, getCurrentAge } from '@core/models/life-plan.model';
import { LiteRequest, LiteResponse, deepCloneLiteRequest } from '@core/models/lite/lite.model';
import { NormalProduct, Product, adjustValuesToCurrency, deepCloneProduct } from '@core/models/product.model';
import { getProductAvailableTemporalities } from '@core/models/temporality.model';
import { environment } from '@environment';
import { Store } from '@ngrx/store';
import { AppState } from '@redux/app.reducers';
import { Observable, merge, throwError } from 'rxjs';
import { map, switchMap, toArray } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { CurrencyExchange } from '@core/models/currency-exchange';
import { PredictionsPerTarget } from '@core/models/predictions.model';
import { CurrencyAmount, DummyCurrencyAmount, substractCurrencyAmount, sumCurrencyAmount } from '@core/models/currency-amount.model';
import { InitalSaving } from 'src/app/pages/life-plan/components/product-graphic-status/product-graphic-status.component';
import { Insurance } from '@core/constants/data-types.constants';
import { SavingGeneralProduct } from 'src/app/pages/life-plan/components/product-manager/product-manager.component';

export interface LiteResponsesWithLimits {
  feeMaxLimitResponse: LiteResponse;
  amountMinLimitResponse: LiteResponse;
  currentResponse: LiteResponse;
}

export interface LiteResponsesWithLimitsPerInsuranceTarget {
  insuranceTarget: InsuranceTarget;
  liteResponses: LiteResponsesWithLimits;
}

export interface InitialGoalAndMaxAmount {
  userInitial: CurrencyAmount,
  spouseInitial: CurrencyAmount,
  goal: CurrencyAmount,
  userMax: CurrencyAmount,
  spouseMax: CurrencyAmount,
  savingMax?: CurrencyAmount
}

@Injectable({
  providedIn: 'root'
})
export class LiteService {
  lifePlan!: LifePlan;
  currencyExchange?: CurrencyExchange;
  predictions!: { [key: string]: PredictionsPerTarget };
  userInitialSavings?: any;
  spouseInitialSavings? : any;
  isCoverageValueInsuredAmount?: boolean = true;
  coverageValueInsuredAmount: EventEmitter<boolean> = new EventEmitter<boolean>();
  currentAge: number = 0;
  currentTargetId?: string;

  constructor(private http: HttpClient, private configService: ConfigService, private store: Store<AppState>) {
    this.store.subscribe({
      next: state => {
        this.lifePlan = state.lifePlan.currentLifePlan;
        this.currencyExchange = state.config.currencyExchange;
        this.predictions = state.predictions.current;
        this.userInitialSavings = state.lifePlan.currentLifePlan.user?.insurances?.currentInsurances;
        this.spouseInitialSavings = state.lifePlan.currentLifePlan.spouse?.insurances?.currentInsurances;
        this.currentAge = getCurrentAge(state.lifePlan.currentLifePlan.user?.personal?.birthDate!);
        this.currentTargetId = state.lifePlan.currentTargetId;
      }
    });
    this.coverageValueInsuredAmount.subscribe(
      isCoverageValueInsuredAmount => (this.isCoverageValueInsuredAmount = isCoverageValueInsuredAmount)
    );
  }

  calculate(liteId: string, liteRequest: LiteRequest): Observable<LiteResponse> {
    if (!liteId) {
      return throwError('Invalid LITE id');
    }
    return this.http.post<LiteResponse>(`${environment.financialPlanner.url}/lite/${liteId}`, liteRequest);
  }

  generateTargetLiteRequest(
    product: Product,
    insuranceTarget: InsuranceTarget,
    insuredAmount?: number,
    feeAmount?: number
  ): LiteRequest {
    const contractor = insuranceTarget.policyHolder;
    const insured = insuranceTarget.insured;
    const goalAge = insuranceTarget.milestone?.userAge;

    if (!product) {
      throw new Error('There is no current product from which to obtain LITE data');
    }
    let currencyLiteCode = this.configService.config.sourceConfigurations?.currencyOptions.find(
      c => c.identifier === product.selectedCurrency
    )?.params.tarificationCode;
    if (!currencyLiteCode) {
      // This is being applied to avoid assigning an empty or undefined value when it is not in the configuration.
      currencyLiteCode =
        this.configService.config.sourceConfigurations?.currencyOptions[0].params.tarificationCode || '';
    }
    const productAvailableTemporalities = getProductAvailableTemporalities(
      product,
      product.selectedCurrency,
      insured.age
    );
    if (!productAvailableTemporalities || productAvailableTemporalities.length === 0) {
      const error = `No available temporalities found for product ${product.id} with currency ${product.selectedCurrency} and insured age being ${insured.age}`;
      console.error(error);
      throw new Error(error);
    }
    let selectedTemporality = productAvailableTemporalities.find(t => t.id === product?.selectedTemporality);
    if (!selectedTemporality) {
      // This is being applied to avoid assigning an empty or undefined value when it is not in the configuration.
      selectedTemporality = productAvailableTemporalities[0];
    }
    let genderLiteCode =
      this.configService.config.sourceConfigurations?.genreOptions.find(g => g.identifier === contractor.gender)?.params
        .tarificationCode || '';
    if (!genderLiteCode) {
      // This is being applied to avoid assigning an empty or undefined value when it is not in the configuration.
      genderLiteCode = this.configService.config.sourceConfigurations?.genreOptions[0].params.tarificationCode || '';
    }

    // WIP: New superación lite
    let spouseAge;

    if (this.lifePlan.spouse && this.lifePlan.spouse.personal && this.lifePlan.spouse.personal.birthDate) {
      spouseAge = getCurrentAge(this.lifePlan.spouse.personal.birthDate);
    }

    let includePolicyFee = true;
    if (product.insurance === 'EDUCATION') {
      includePolicyFee = this.isFirstChildOfLite(this.lifePlan.insurances!, product.id, insuranceTarget.id);
    }

    let investorProfileLiteCode =
      this.configService.config.sourceConfigurations?.investorProfileOptions.find(
        p => p.identifier === contractor.investorProfile
      )?.params.matching || '';
    if (!investorProfileLiteCode) {
      // This is being applied to avoid assigning an empty or undefined value when it is not in the configuration.
      investorProfileLiteCode =
        this.configService.config.sourceConfigurations?.investorProfileOptions[0]?.params.matching || '';
    }
    const liteRequest: LiteRequest = {
      age: contractor.age,
      minorsAge: insured.age,
      goalAge: goalAge,
      currency: currencyLiteCode,
      spouseAge: spouseAge,
      includePolicyFee: includePolicyFee,
      gender: genderLiteCode,
      temporality: selectedTemporality.value,
      temporalityType: selectedTemporality.type,
      increase: product.selectedIncrease?.percentage,
      investorProfile: investorProfileLiteCode,
      frequency: this.lifePlan.frequency,
      projectedInvestment: {
        amount: product.projectedInvestment?.amount,
        projection: product.projectedInvestment?.projection,
        active: product.projectedInvestment?.active
      },
      basic: {
        insuredSum: insuredAmount,
        feeAmount
      },
      isLimit: false
    };
    // switch (product.type) {
    //   case 'normal':
    //     liteRequest.feeFrequency = 'Anual';
    //     break;
    //   case 'investment':
    //     liteRequest.feeFrequency = 'Prima Unica';
    //     break;
    // }
    product.coverages
      .filter(c => c.active)
      .forEach(c => {
        liteRequest[c.id] = {
          active: true,
          insuredSum: c.isAmountModifiable
            ? this.isCoverageValueInsuredAmount
              ? insuredAmount
              : c.currentAmount?.amount
            : undefined
        };
      });
    return liteRequest;
  }

  // WIP: New superación lite
  isFirstChildOfLite(insurances: LifePlanInsurances, productId: string, insuredId: string) {
    if (!insurances) {
      return false;
    }
    const insuredSameProduct = Object.keys(insurances)
      .sort((a, b) => insurances[a].order - insurances[b].order)
      .filter(insured => {
        return insurances[insured].currentProduct && insurances[insured].currentProduct?.id === productId;
      });
    return insuredSameProduct[0] === insuredId;
  }

  calculateProduct(product: Product, insuranceTarget: InsuranceTarget, insuredAmount?: number, payAmount?: number) {
    return this.calculate(
      product.liteId,
      this.generateTargetLiteRequest(product, insuranceTarget, insuredAmount, payAmount)
    );
  }

  calculateTarget(
    insuranceTarget: InsuranceTarget,
    insuredAmount?: number,
    payAmount?: number
  ): Observable<LiteResponse> {
    const product = insuranceTarget.currentProduct;
    if (!product) {
      return throwError('There is no current product from which to obtain LITE data');
    }
    return this.calculateProduct(product, insuranceTarget, insuredAmount, payAmount);
  }

  calculateProductWithLimits(
    product: Product,
    insuranceTarget: InsuranceTarget,
    insuredAmount?: number,
    payAmount?: number
  ) {
    const liteRequest = this.generateTargetLiteRequest(product, insuranceTarget, insuredAmount, payAmount);

    const amountMinLimitRequest: LiteRequest = deepCloneLiteRequest(liteRequest);
    amountMinLimitRequest.basic.insuredSum = undefined;
    amountMinLimitRequest.isLimit = true;
    const feeMaxLimitRequest: LiteRequest = deepCloneLiteRequest(liteRequest);
    feeMaxLimitRequest.basic.feeAmount = undefined;
    feeMaxLimitRequest.isLimit = true;
    const initialGoalAndMaxAmount = this.getInitialGoalAndMaxAmount(product);
    if(insuranceTarget.insurance === "SAVING") {
      const savingInsuranceTarget = insuranceTarget as SavingInsuranceTarget;
      if(savingInsuranceTarget.totalAmount) {
        feeMaxLimitRequest.max = savingInsuranceTarget.assuredAmount.amount;
      }
    } else {
      if(insuranceTarget.id === "death-spouse" || insuranceTarget.id === "retirement-spouse"){
        feeMaxLimitRequest.max = initialGoalAndMaxAmount.spouseMax.amount;
      } else {
        feeMaxLimitRequest.max = initialGoalAndMaxAmount.userMax.amount;
      }
    }

    switch (product.type) {
      case 'investment':
        amountMinLimitRequest.basic.feeAmount = product.investedAmountLimits.min.amount;
        feeMaxLimitRequest.basic.insuredSum = product.amountAtEndOfTemporalityLimits.max.amount;
        break;
      case 'normal':
        amountMinLimitRequest.basic.feeAmount = product.feeLimits.min.amount;
        feeMaxLimitRequest.basic.insuredSum = product.assuredAmountLimits.max.amount;

        ///Max/min saving
        if(this.lifePlan.insurances){
          
          const initialGoalAndMaxAmount = this.getInitialGoalAndMaxAmount(product);
          const savingGeneralProduct = this.calculateGeneralSavingValues(this.lifePlan);

          if(insuranceTarget.insurance === "SAVING") {
            const savingInsuranceTarget = insuranceTarget as SavingInsuranceTarget;
            if(savingInsuranceTarget.totalAmount) {
              feeMaxLimitRequest.basic.insuredSum = savingInsuranceTarget.assuredAmount.amount;
            } else {
              feeMaxLimitRequest.basic.insuredSum = savingGeneralProduct.assuredAmountMax.amount;
              feeMaxLimitRequest.basic.feeAmount = savingGeneralProduct.feeMax.amount;
            }
          } else {
            if(insuranceTarget.id === "death-spouse" || insuranceTarget.id === "retirement-spouse"){
              feeMaxLimitRequest.basic.insuredSum = initialGoalAndMaxAmount.spouseMax.amount;
            } else {
              feeMaxLimitRequest.basic.insuredSum = initialGoalAndMaxAmount.userMax.amount;
            }
          }
        }
        break;
    }

    const calculateLiteAmountMinLimit$ = this.calculate(product.liteId, amountMinLimitRequest).pipe(
      map(liteResponse => {
        const responses: Partial<LiteResponsesWithLimits> = {
          amountMinLimitResponse: liteResponse
        };
        return responses;
      })
    );
    const calculateLiteFeeMaxLimit$ = this.calculate(product.liteId, feeMaxLimitRequest).pipe(
      map(liteResponse => {
        const responses: Partial<LiteResponsesWithLimits> = {
          feeMaxLimitResponse: liteResponse
        };
        return responses;
      })
    );
    // We need to call first for the limits
    return merge(calculateLiteAmountMinLimit$, calculateLiteFeeMaxLimit$).pipe(
      toArray(),
      switchMap(responses => {
        // Then we need to check if the current values that the user has set are
        // between the new limits (that could have change due to temporality or
        // currency changes) and, if not, then set them accordingly and make the
        // request with the adjusted values
        let liteResponses: Partial<LiteResponsesWithLimits> = {};
        for (const response of responses) {
          liteResponses = { ...liteResponses, ...response };
        }
        const amountAndFeeMinLimits = liteResponses.amountMinLimitResponse?.basic;
        const amountAndFeeMaxLimits = liteResponses.feeMaxLimitResponse?.basic;
        if (amountAndFeeMinLimits && amountAndFeeMaxLimits) {
          const basicInsuredSum = liteRequest.basic.insuredSum;
          const basicFeeAmount = liteRequest.basic.feeAmount;
          // Modify the liteRequest accordingly to the limits
          if (basicInsuredSum !== undefined && basicInsuredSum !== null) {
            if (basicInsuredSum < amountAndFeeMinLimits.insuredSum) {
              liteRequest.basic.insuredSum = amountAndFeeMinLimits.insuredSum;
            } else if (basicInsuredSum > amountAndFeeMaxLimits.insuredSum) {
              liteRequest.basic.insuredSum = amountAndFeeMaxLimits.insuredSum;
            }
          } else if (basicFeeAmount !== undefined && basicInsuredSum !== null) {
            if (basicFeeAmount < amountAndFeeMinLimits.feeAmount) {
              liteRequest.basic.feeAmount = amountAndFeeMinLimits.feeAmount;
            } else if (basicFeeAmount > amountAndFeeMaxLimits.feeAmount) {
              liteRequest.basic.feeAmount = amountAndFeeMaxLimits.feeAmount;
            }
          }
          if(insuranceTarget.insurance === "SAVING" && this.lifePlan.insurances) {
            const savingInsuranceTarget = insuranceTarget as SavingInsuranceTarget;
            if(!savingInsuranceTarget.totalAmount) {
              const savingGeneralProduct = this.calculateGeneralSavingValues(this.lifePlan);
              liteRequest.basic.insuredSum = savingGeneralProduct.assuredAmount.amount;
            }
          }
        }
        // Make the 'current' request once limits have been adjusted
        return this.calculate(product.liteId, liteRequest).pipe(
          map(liteResponse => {
            liteResponses = {
              ...liteResponses,
              ...{
                currentResponse: liteResponse
              }
            };
            return liteResponses as LiteResponsesWithLimits;
          })
        );
      })
    );
  }

  getInitialGoalAndMaxAmount(product?: Product, totalAmount?: CurrencyAmount): InitialGoalAndMaxAmount {
    let userInitialSavingResult :CurrencyAmount = DummyCurrencyAmount;
    let spouseInitialSavingResult :CurrencyAmount = DummyCurrencyAmount;
    let goalSaving: CurrencyAmount = DummyCurrencyAmount;
    
    if(this.userInitialSavings){
      this.userInitialSavings.forEach((userInitialSaving: InitalSaving)  => {
        if(product?.insurance === userInitialSaving.type){
          switch(userInitialSaving.type){
            case "EDUCATION":
              userInitialSavingResult = userInitialSaving.assuredAmount!; 
              break;
            case "DEATH":
              userInitialSavingResult = userInitialSaving.assuredAmount!;
              break;
            case "RETIREMENT":
              userInitialSavingResult = userInitialSaving.assuredAmount!;
              break;
            case "SAVING":
              userInitialSavingResult = userInitialSaving.assuredAmount!;
              break;
            default:
              break;
          }
        }
      });
    }

    if(this.spouseInitialSavings){
      this.spouseInitialSavings.forEach((spouseInitialSaving: InitalSaving)  => {
        if(product?.insurance === spouseInitialSaving.type){
          switch(spouseInitialSaving.type){
            case "EDUCATION":
              spouseInitialSavingResult = spouseInitialSaving.assuredAmount!; 
              break;
            case "DEATH":
              spouseInitialSavingResult = spouseInitialSaving.assuredAmount!;
              break;
            case "RETIREMENT":
              spouseInitialSavingResult = spouseInitialSaving.assuredAmount!;
              break;
            case "SAVING":
              userInitialSavingResult = spouseInitialSaving.assuredAmount!;
              break;
            default:
              break;
          }
        }
      });
    }

    Object.keys(this.predictions).forEach(key => {
      const activePrediction = this.predictions[key];
      if(product?.insurance === activePrediction.insurance){
        switch(activePrediction.insurance){
          case "EDUCATION":
            goalSaving = activePrediction.educationSaving;
            break;
          case "DEATH":
            goalSaving = activePrediction.deathProtection;
            break;
          case "SAVING":
            const savingProduct = product as NormalProduct;
            goalSaving = savingProduct.assuredAmountLimits.max
            break;
          case "RETIREMENT":
            if(this.currentTargetId){
              const retirementPrediction = this.predictions[this.currentTargetId];
              if(retirementPrediction.insurance === "RETIREMENT") goalSaving = retirementPrediction.retirementPurchasingPower;
            } else {
              const retirementPrediction = this.predictions['retirement-user'];
              if(retirementPrediction.insurance === "RETIREMENT") goalSaving = retirementPrediction.retirementPurchasingPower;
            }
            break;
          default:
            break;
        }
      }
    });

    const initialGoalAndMaxAmount = {
      userInitial: userInitialSavingResult,
      spouseInitial: spouseInitialSavingResult,
      goal: goalSaving,
      userMax: {
        amount: goalSaving && userInitialSavingResult ? goalSaving.amount - userInitialSavingResult.amount : 0,
        currency: "EUR"
      },
      spouseMax: {
        amount: goalSaving && spouseInitialSavingResult ? goalSaving.amount - spouseInitialSavingResult.amount : 0,
        currency: "EUR"
      },
      savingMax: totalAmount
    }

    return initialGoalAndMaxAmount ;
  }


  calculateProductHaveCoverage(product: Product) : string {
    if(this.getInitialGoalAndMaxAmount(product).userInitial.amount > 0) return "-coverage";
    if(this.getInitialGoalAndMaxAmount(product).spouseInitial.amount > 0) return "-coverage";
    return "";
  }

  calculateGeneralSavingValues(lifePlan : LifePlan) : SavingGeneralProduct {
    let assuredAmount: CurrencyAmount = DummyCurrencyAmount;
    let fee: CurrencyAmount = DummyCurrencyAmount;
    let assuredAmountMax: CurrencyAmount = DummyCurrencyAmount;
    let feeMax: CurrencyAmount = DummyCurrencyAmount;
    let savings: CurrencyAmount = DummyCurrencyAmount;

    Object.keys(lifePlan?.insurances!).forEach(key => { 
      if(lifePlan?.insurances![key].insurance ==="SAVING" && lifePlan?.insurances![key].id !== "saving-general" && lifePlan?.insurances![key].id !== "saving-investment"){
        const savingInsurance = lifePlan?.insurances![key] as SavingInsuranceTarget;
        const savingProduct = savingInsurance.currentProduct as NormalProduct;

        if(savingProduct) {
          assuredAmount = sumCurrencyAmount(assuredAmount, savingProduct.currentAssuredAmount);
          fee = sumCurrencyAmount(fee, savingProduct.currentFee);
        }
        savings = sumCurrencyAmount(savings, savingInsurance.savings);
        assuredAmountMax = sumCurrencyAmount(assuredAmountMax, savingInsurance.totalAmount);
      }
    })
    assuredAmountMax = substractCurrencyAmount(assuredAmountMax, savings);
    const savingGeneralValues = {
      assuredAmount: assuredAmount,
      fee: fee,
      assuredAmountMax: assuredAmountMax,
      feeMax: feeMax,
      savings: savings
    }    

    return savingGeneralValues;
  }

  calculateTargetWithLimits(
    insuranceTarget: InsuranceTarget,
    insuredAmount?: number,
    payAmount?: number
  ): Observable<LiteResponsesWithLimits> {
    const product = insuranceTarget.currentProduct;
    if (!product) {
      return throwError('There is no current product from which to obtain LITE data');
    }
    return this.calculateProductWithLimits(product, insuranceTarget, insuredAmount, payAmount);
  }

  calculateAllTargetsWithLimitsFromLifePlan(lifePlan: LifePlan) {
    const liteObservables: Observable<LiteResponsesWithLimitsPerInsuranceTarget>[] = [];
    const lifePlanInsurances = lifePlan.insurances;
    if (lifePlanInsurances) {
      Object.entries(lifePlanInsurances).forEach(([id, insuranceTarget]) => {
        const product = insuranceTarget.currentProduct;
        if (product) {
          let insuredAmount;
          let feeAmount;
          switch (product.type) {
            case 'investment':
              if (product.amountAtEndOfTemporality) {
                insuredAmount = product.amountAtEndOfTemporality.amount;
              }
              if (product.currentInvestment) {
                feeAmount = product.currentInvestment.amount;
              }
              break;
            case 'normal':
              if (product.insurance === 'EDUCATION') {
                adjustValuesToCurrency(product, product.selectedCurrency, this.currencyExchange!);
              }
              if (product.currentAssuredAmount) {
                insuredAmount = product.currentAssuredAmount.amount;
              }
              if (product.currentFee) {
                feeAmount = product.currentFee.amount;
              }
              break;
          }
          liteObservables.push(
            this.calculateTargetWithLimits(insuranceTarget, insuredAmount, feeAmount).pipe(
              map(liteResponses => ({ insuranceTarget, liteResponses }))
            )
          );
        }
      });
    }
    return merge(...liteObservables);
  }
}
